import { Badge } from "@/design-system/Badge";
import { isDefined } from "@/helpers/util";

import { ArrowNarrowRightIcon } from "../Icons/ArrowNarrowRightIcon";
import { TrendDown01Icon } from "../Icons/TrendDown01Icon";
import { TrendUp01Icon } from "../Icons/TrendUp01Icon";

const LABEL_PLACEHOLDER = "---";

type TrendBadgeProps = {
  value: number;
  referenceValue?: number;
  isTrendInversed?: boolean;
  isZeroOmitted?: boolean;
  shouldRoundUp?: boolean;
} & (
  | {
      method?: "raw";
      format?: "percentage";
    }
  | {
      method: "percentage";
      format?: never;
    }
);

export function TrendBadge({
  value,
  referenceValue,
  method = "raw",
  format,
  isZeroOmitted = false,
  isTrendInversed = false,
  shouldRoundUp = false,
}: TrendBadgeProps): React.ReactNode {
  const rawDiffValue = value - (referenceValue || 0);

  let perceivedValue: number;
  if (method === "raw") {
    perceivedValue = shouldRoundUp
      ? Math.sign(rawDiffValue) * Math.ceil(Math.abs(rawDiffValue))
      : Number.parseFloat(rawDiffValue.toFixed(1));
  } else {
    const rawDiffPercentageValue = referenceValue ? ((value - referenceValue) / referenceValue) * 100 : 0;

    perceivedValue = shouldRoundUp
      ? Math.sign(rawDiffPercentageValue) * Math.ceil(Math.abs(rawDiffPercentageValue))
      : Number.parseFloat(rawDiffPercentageValue.toFixed(1));
  }

  if (isZeroOmitted && perceivedValue === 0) {
    return null;
  }

  const isPositive = isTrendInversed ? perceivedValue < 0 : perceivedValue > 0;
  const isNegative = isTrendInversed ? perceivedValue > 0 : perceivedValue < 0;
  let label: string;
  let icon = <ArrowNarrowRightIcon data-testid="arrow-narrow-right-icon" />;
  let variant: "success" | "danger" | "info" = "info";
  if (isPositive) {
    icon = <TrendUp01Icon data-testid="trend-up-icon" />;
    variant = "success";
  } else if (isNegative) {
    icon = <TrendDown01Icon data-testid="trend-down-icon" />;
    variant = "danger";
  }

  if (method === "raw") {
    if (!isDefined(referenceValue)) {
      label = LABEL_PLACEHOLDER;
    } else if (perceivedValue > 0) {
      label = `+${perceivedValue}`;
    } else if (perceivedValue < 0) {
      label = `${perceivedValue}`;
    } else {
      label = `${perceivedValue}`;
    }

    if (format === "percentage" && label !== LABEL_PLACEHOLDER) {
      label = `${label}%`;
    }
  } else {
    if (!isDefined(referenceValue)) {
      label = LABEL_PLACEHOLDER;
    } else if (perceivedValue > 999) {
      perceivedValue = Math.min(perceivedValue, 999);
      label = `+${perceivedValue}%`;
    } else if (perceivedValue < -999) {
      perceivedValue = Math.max(perceivedValue, -999);
      label = `${perceivedValue}%`;
    } else if (perceivedValue > 0) {
      label = `+${perceivedValue}%`;
    } else if (perceivedValue < 0) {
      label = `${perceivedValue}%`;
    } else {
      label = `${perceivedValue}%`;
    }
  }

  return (
    <Badge icon={icon} variant={variant}>
      {label}
    </Badge>
  );
}
